import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Params } from '@angular/router';
import { deserialize, dataMember, required, typedArray } from 'santee-dcts';
import { Settings } from '../core';
import { Observable } from 'rxjs';
import { map, switchMap, publishLast, refCount } from 'rxjs/operators';
import { findIndex } from 'lodash';

export class UserFilter {
    @dataMember()
    @required()
    public id: string;

    @dataMember()
    @required({ nullable: true })
    public siteId: number;

    @dataMember()
    @required({ nullable: true })
    public protocolId: number;

    @dataMember()
    @required()
    public name: string;

    @dataMember()
    @required()
    public group: string;

    @dataMember()
    public params: Params;
}

export class UserFiltersReadModel {
    @dataMember()
    @required()
    @typedArray(UserFilter)
    public filters: UserFilter[];
}

@Injectable()
export class FiltersStorage {
    private filtersValue: UserFilter[] = null;
    private isLoadedValue: Observable<boolean> = null;

    constructor(
        private settings: Settings,
        private http: HttpClient
    ) { }

    public getFilters(group: string, siteId: number, protocolId: number) {
        return this.filtersValue ? this.filtersValue.filter(f => f.group === group && f.siteId === siteId && f.protocolId === protocolId) : null;
    }

    public save(filter: UserFilter): Promise<UserFilter> {
        return this.isLoaded
            .pipe(
                switchMap(() => {
                    return this.http.post(`${this.settings.apiUrl}/user-filters/`, filter);
                }),
                map((rawData) => {
                    const responseFilter = deserialize(rawData, UserFilter);
                    if (!filter.id) {
                        this.filtersValue.push(responseFilter);
                    } else {
                        const index = findIndex(this.filtersValue, f => f.id === filter.id);
                        this.filtersValue[index] = responseFilter;
                    }
                    return responseFilter;
                })
            )
            .toPromise();
    }

    public delete(id: string): Promise<void> {
        return this.isLoaded
            .pipe(
                switchMap(() => {
                    return this.http.delete(`${this.settings.apiUrl}/user-filters/${id}`);
                }))
            .forEach(() => {
                this.filtersValue = this.filtersValue.filter(f => f.id !== id);
            });
    }

    private loadFilters() {
        return this.http.get(`${this.settings.apiUrl}/user-filters`)
            .pipe(
                map((rawData) => {
                    this.filtersValue = deserialize(rawData, UserFiltersReadModel).filters.sort((a, b) => a.name.localeCompare(b.name));
                    return true;
                }),
                publishLast(),
                refCount()
            );
    }

    public get isLoaded(): Observable<boolean> {
        if (!this.isLoadedValue) {
            this.isLoadedValue = this.loadFilters();
        }

        return this.isLoadedValue;
    }
}